import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCanCheck } from "@/hooks/use-permission";
import { SOLICITATION_SCREEN_REQUIREMENTS } from "@/lib/solicitation-permissions";
import {
  LayoutDashboard,
  Users,
  Shield,
  Settings,
  Wallet,
  FileText,
  X,
  Home,
  ArrowLeftRight,
  CircleDollarSign,
  HandCoins,
  Landmark,
  AlertTriangle,
  Key,
  Link2,
  UserCog,
  ExternalLink,
  Activity,
  ScrollText,
  ShieldCheck,
  Map,
  Scale,
} from "lucide-react";
import type { ComponentType } from "react";
import type { PermissionRequirement } from "@/types/rbac.types";

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

interface MenuItem {
  id: string;
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  roles?: PermissionRequirement[];
}

interface MenuSection {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  items: MenuItem[];
  roles?: PermissionRequirement[];
}

interface ExternalLinkItem {
  id: string;
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  external: true;
}

// Mapeamento de visibilidade espelhando o dash antigo:
// ADMIN → ADMINISTRACAO, MANAGER → BACKOFFICE, ANALYST → VIEWER
const MENU_SECTIONS: MenuSection[] = [
  {
    id: "principal",
    label: "Principal",
    icon: Home,
    items: [
      // Resumo: ADMIN, MANAGER (ANALYST ve dashboard mas é redirecionado pra statuspage)
      { id: "resumo", label: "Resumo", href: "/", icon: LayoutDashboard, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
      // Transações: ADMIN, MANAGER, SUPPORT
      { id: "transacoes", label: "Transações", href: "/transacoes", icon: ArrowLeftRight, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
      // Clientes: ADMIN, MANAGER, SUPPORT
      { id: "clientes", label: "Clientes", href: "/clientes", icon: Users, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
    ],
  },
  {
    id: "operacoes",
    label: "Operações",
    icon: FileText,
    // Seção inteira: ADMIN, MANAGER
    roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }],
    items: [
      // Saldos: ADMIN, MANAGER
      { id: "saldos", label: "Extrato de Saldos", href: "/saldos", icon: Landmark, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
      // Solicitações (CAAS-4550): mesma constante da rota em App.tsx. Menu e rota
      // divergentes é o defeito que esta task consertou — não reescrever à mão.
      { id: "solicitacoes", label: "Solicitações", href: "/solicitacoes", icon: HandCoins, roles: SOLICITATION_SCREEN_REQUIREMENTS },
      // Saque: ADMIN, MANAGER
      { id: "saque", label: "Saque", href: "/saque", icon: CircleDollarSign, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
      // Tesouraria: ADMIN, MANAGER
      { id: "tesouraria", label: "Tesouraria", href: "/tesouraria", icon: Wallet, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
      // Conciliação (CAAS-4014 liqi-as-exchange): ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE (Itaú-facing)
      { id: "conciliacoes", label: "Conciliação", href: "/conciliacoes", icon: Scale, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
    ],
  },
  {
    id: "suporte",
    label: "Suporte",
    icon: AlertTriangle,
    items: [
      // Roadmap (embed do board): qualquer role autenticado
      { id: "roadmap", label: "Roadmap", href: "/embed/roadmap", icon: Map },
      // Chamados (embed do board): qualquer role autenticado
      { id: "chamados", label: "Chamados", href: "/embed/locs", icon: AlertTriangle },
      // Status Page: todos
      { id: "status-page", label: "Status Page", href: "/status-page", icon: Activity },
    ],
  },
];

// Equipe: ADMIN, MANAGER → ADMINISTRACAO, BACKOFFICE
const ADMIN_SECTION: MenuSection = {
  id: "admin",
  label: "Administração",
  icon: UserCog,
  roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }],
  items: [
    { id: "equipe", label: "Equipe", href: "/equipe", icon: Users },
  ],
};

// Config: SSO e API Keys = ADMIN only, Conexões = ADMIN, MANAGER
const CONFIG_CHIPS: MenuItem[] = [
  { id: "sso", label: "SSO", href: "/configuracoes/sso", icon: Shield, roles: [{ role: "ADMINISTRACAO" }] },
  { id: "api-keys", label: "API", href: "/configuracoes/api-keys", icon: Key, roles: [{ role: "ADMINISTRACAO" }] },
  { id: "conexoes", label: "Conexões", href: "/configuracoes/conexoes", icon: Link2, roles: [{ role: "ADMINISTRACAO" }, { role: "BACKOFFICE" }] },
];

const EXTERNAL_LINKS: ExternalLinkItem[] = [
  { id: "docs-api", label: "Documentação API", href: "https://liqi-caas.readme.io/", icon: ExternalLink, external: true },
];

// Links legais (PDFs estaticos em /public/documents). Abrem em nova aba.
const LEGAL_LINKS: ExternalLinkItem[] = [
  { id: "termos-uso", label: "Termos de uso", href: "/documents/termo-de-uso.pdf", icon: ScrollText, external: true },
  { id: "politica-privacidade", label: "Política de privacidade", href: "/documents/politica-de-privacidade-de-dados.pdf", icon: ShieldCheck, external: true },
];

export default function Sidebar({ isOpen, setIsOpen }: SidebarProps) {
  const [location] = useLocation();
  const can = useCanCheck();

  const isActive = (href: string): boolean => {
    if (href === "/") return location === "/";
    return location === href || location.startsWith(href + "/");
  };

  // Filtra seções e items pela role do user
  const allSections = [...MENU_SECTIONS, ADMIN_SECTION];
  const visibleSections = allSections
    .filter((section) => !section.roles || can(section.roles))
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.roles || can(item.roles),
      ),
    }))
    .filter((section) => section.items.length > 0);

  const visibleConfigChips = CONFIG_CHIPS.filter((chip) => !chip.roles || can(chip.roles));
  const showConfigSection = visibleConfigChips.length > 0;

  return (
    <div
      className={cn(
        "fixed top-0 bottom-0 left-0 z-50 flex flex-col w-64 bg-sidebar border-r border-sidebar-border shadow-xl transition-all duration-300 ease-in-out lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full",
      )}
      style={{ height: "100dvh" }}
    >
      {/* Logo */}
      <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-sidebar-border">
        <div className="flex items-center">
          <img
            src="/assets/logo-sidebar.svg"
            alt="Liqi"
            className="w-full max-w-[160px] h-auto"
          />
        </div>
        {/* CAAS-4306: so renderiza com sidebar aberta — evita conflito com 'Abrir Menu' do header em mobile */}
        {isOpen && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-7 w-7 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Fechar Menu</span>
          </Button>
        )}
      </div>

      {/* Navigation — flat, no accordion. CAAS-4312: <nav> landmark com id pra aria-controls do toggle */}
      <ScrollArea className="flex-1 px-3 py-4" data-sidebar="content">
        <nav id="primary-nav" aria-label="Menu principal" className="space-y-6">
          {visibleSections.map((section) => (
            <div key={section.id}>
              {/* Section Label */}
              <div className="flex items-center gap-2 px-3 pb-3 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                <section.icon className="h-3.5 w-3.5" />
                {section.label}
              </div>

              {/* Section Items — always visible */}
              {section.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    className={cn(
                      "w-full flex items-center justify-start rounded-xl px-3 py-2.5 text-sm font-medium transition-all mb-1",
                      active
                        ? "bg-sidebar-primary text-white shadow-sm"
                        : "text-sidebar-foreground hover:bg-sidebar-primary/20",
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    <item.icon className="h-4 w-4 text-current" />
                    <span className="ml-2">{item.label}</span>
                    {active && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse" />
                    )}
                  </Link>
                );
              })}
            </div>
          ))}

          {/* External Links */}
          <div className="mt-4 pt-4 border-t border-sidebar-border/30">
            {EXTERNAL_LINKS.map((item) => (
              <a
                key={item.id}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-start rounded-xl px-2 py-2 text-sm font-medium transition-all mb-1 text-sidebar-foreground hover:bg-sidebar-primary/20"
              >
                <item.icon className="h-4 w-4 text-current" />
                <span className="ml-2">{item.label}</span>
                <ExternalLink className="ml-auto h-3 w-3 opacity-50" />
              </a>
            ))}
          </div>

          {/* Legal Links — Termos de uso e Política de privacidade (CAAS-4233/4238) */}
          <div className="pt-3 border-t border-sidebar-border/30" data-sidebar="legal-links">
            {LEGAL_LINKS.map((item) => (
              <a
                key={item.id}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-start rounded-lg px-2 py-1.5 text-xs font-medium transition-all mb-0.5 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-primary/10"
              >
                <item.icon className="h-3.5 w-3.5 text-current" />
                <span className="ml-2">{item.label}</span>
              </a>
            ))}
          </div>
        </nav>
      </ScrollArea>

      {/* Footer — config chips */}
      {showConfigSection && (
        <div className="flex-shrink-0 border-t-2 border-sidebar-border bg-sidebar/80 px-3 py-4">
          <div className="flex items-center gap-1.5 px-2 pb-2 text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
            <Settings className="h-3.5 w-3.5" />
            Config
          </div>
          <div className="flex flex-wrap gap-2">
            {visibleConfigChips.map((chip) => {
              const active = isActive(chip.href);
              return (
                <Link
                  key={chip.id}
                  href={chip.href}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all",
                    active
                      ? "bg-sidebar-primary text-white"
                      : "bg-sidebar-border/50 text-sidebar-foreground/70 hover:bg-sidebar-primary/20 hover:text-sidebar-foreground",
                  )}
                  onClick={() => setIsOpen(false)}
                >
                  <chip.icon className="h-3 w-3" />
                  {chip.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
